import { applyDefaultOrder, applyValidOrderToLayerData } from '../layers/DefaultOrder.js';
import { fire } from '../utils/events.js';
import Layer from '../layers/Layer.js';
import SlideManager from '../utils/slide-manager.js';
import CylinderInfo from '../2DObjects/CylinderInfo.js';

function create2DLayer(name, opts = {}) {
    let index = Q3.layers.graph.length;

    if (typeof opts === "boolean") {
        opts = { isText: opts };
    }
    const l = new Layer({
        type: '2D',
        isText: opts.isText,
        subtype: opts.subtype,
        index: index,
        reordered: false,
        name: name ? name : 'Layer ' + Q3.layers.graph.length,
    }, true);

    Q3.layers.list[l.uuid] = l;

    let layerData = {
        visible: true,
        index: l.index,
        reordered: l.reordered,
        name: l.name,
        parent: l.parent,
        type: l.type,
        subtype: l.subtype,
        objects: {},
    };

    Q3.data.layers.layers2D[l.uuid] = layerData;

    SlideManager.currentSlide.layers[l.uuid] = true;

    Q3.layers.update();

    return l;
}

function create2DLayerData(name, opts, slide) {
    if (typeof opts === "boolean") {
        opts = { isText: opts };
    }

    let layerData = {
        visible: true,
        index: opts.index,
        reordered: false,
        name: name,
        type: '2D',
        subtype: opts.subtype,
        objects: {},
    };

    let uuid = THREE.Math.generateUUID();

    Q3.data.layers.layers2D[uuid] = layerData;
    Q3.data.slides[slide].layers[uuid] = true;
    Q3.data.slides[slide].layers2D[uuid] = 1;

    return layerData;
}

const AddInteractiveUI = {};

AddInteractiveUI.atom = function (atom, slide) {
    if (slide === undefined) slide = SlideManager.activeSlide;

    let uuid;

    if (slide === SlideManager.activeSlide) {
        let index = Q3.layers.graph.length;
        const selectedLayer = create2DLayer('Atom ' + (index + 1), { subtype: 'atomUi' });

        const ui = new Q3.AtomUI({
            atom: atom.uuid,
            index: selectedLayer.objects.length,
        }, selectedLayer, THREE.Math.generateUUID());

        selectedLayer.objects[ui.index] = ui;
        selectedLayer.level = 1;

        const layerData = Q3.data.layers.layers2D[selectedLayer.uuid];
        layerData.objects = layerData.objects || {};

        layerData.objects[ui.uuid] = {
            type: 'atomUi',
            atom: atom.uuid,
        };

        applyDefaultOrder(Q3.layers.graph, selectedLayer.uuid);

        uuid = selectedLayer.uuid;
    } else {
        let index = Q3.layers.minimumUserLayerIndex;
        for (let uuid in Q3.data.slides[slide].layers2D) {
            index++;
        }

        let layerData = create2DLayerData('Atom ' + (index + 1), { subtype: 'atomUi' }, slide);

        uuid = THREE.Math.generateUUID();
        layerData.objects[uuid] = {
            type: 'atomUi',
            atom: atom.uuid,
        };

        applyValidOrderToLayerData(slide);
    }

    return uuid;
}

AddInteractiveUI.emField = function (field, slide) {
    if (slide === undefined) slide = SlideManager.activeSlide;

    let uuid;

    if (slide === SlideManager.activeSlide) {
        let index = Q3.layers.graph.length;
        const selectedLayer = create2DLayer('Electromagnetic Field ' + (index + 1), { subtype: 'emFieldUi' });

        const ui = new Q3.EmFieldUI({
            field: field.uuid,
            index: selectedLayer.objects.length,
        }, selectedLayer, THREE.Math.generateUUID());

        selectedLayer.objects[ui.index] = ui;
        selectedLayer.level = 1;

        const layerData = Q3.data.layers.layers2D[selectedLayer.uuid];
        layerData.objects = layerData.objects || {};

        layerData.objects[ui.uuid] = {
            type: 'emFieldUi',
            field: field.uuid,
        };

        applyDefaultOrder(Q3.layers.graph, selectedLayer.uuid);

        uuid = selectedLayer.uuid;
    } else {
        let index = Q3.layers.minimumUserLayerIndex;
        for (let uuid in Q3.data.slides[slide].layers2D) {
            index++;
        }

        let layerData = create2DLayerData('Electromagnetic Field ' + (index + 1), { subtype: 'emFieldUi' }, slide);

        uuid = THREE.Math.generateUUID();
        layerData.objects[uuid] = {
            type: 'emFieldUi',
            field: field.uuid,
        };

        applyValidOrderToLayerData(slide);
    }

    return uuid;
}

AddInteractiveUI.mmmInfo = function (mmm, slide) {
    if (slide === undefined) slide = SlideManager.activeSlide;

    let uuid;

    if (slide === SlideManager.activeSlide) {
        let index = Q3.layers.graph.length;
        const selectedLayer = create2DLayer('Sample ' + (index + 1), { subtype: 'mmmInfo' });

        const ui = new Q3.MeanMedianModeInfo({
            mmm: mmm.uuid,
            index: selectedLayer.objects.length,
        }, selectedLayer, THREE.Math.generateUUID());

        selectedLayer.objects[ui.index] = ui;
        selectedLayer.level = 1;

        const layerData = Q3.data.layers.layers2D[selectedLayer.uuid];
        layerData.objects = layerData.objects || {};

        layerData.objects[ui.uuid] = {
            type: 'mmmInfo',
            mmm: mmm.uuid,
        };

        applyDefaultOrder(Q3.layers.graph, selectedLayer.uuid);

        uuid = selectedLayer.uuid;
    } else {
        let index = Q3.layers.minimumUserLayerIndex;
        for (let uuid in Q3.data.slides[slide].layers2D) {
            index++;
        }

        let layerData = create2DLayerData('Sample ' + (index + 1), { subtype: 'mmmInfo' }, slide);

        uuid = THREE.Math.generateUUID();
        layerData.objects[uuid] = {
            type: 'mmmInfo',
            mmm: mmm.uuid,
        };

        applyValidOrderToLayerData(slide);
    }

    return uuid;
}

AddInteractiveUI.cylinderInfo = function (cylinder, slide) {
    if (slide === undefined) slide = SlideManager.activeSlide;

    let uuid;

    if (slide === SlideManager.activeSlide) {
        let index = Q3.layers.graph.length;
        const selectedLayer = create2DLayer('Cylinder ' + (index + 1), { subtype: 'cylinderInfo' });

        const ui = new CylinderInfo({
            cylinder: cylinder.uuid,
            index: selectedLayer.objects.length,
        }, selectedLayer, THREE.Math.generateUUID());

        selectedLayer.objects[ui.index] = ui;
        selectedLayer.level = 1;

        const layerData = Q3.data.layers.layers2D[selectedLayer.uuid];
        layerData.objects = layerData.objects || {};

        layerData.objects[ui.uuid] = {
            type: 'cylinderInfo',
            cylinder: cylinder.uuid,
        };

        applyDefaultOrder(Q3.layers.graph, selectedLayer.uuid);

        uuid = selectedLayer.uuid;
    } else {
        let index = Q3.layers.minimumUserLayerIndex;
        for (let uuid in Q3.data.slides[slide].layers2D) {
            index++;
        }

        let layerData = create2DLayerData('Cylinder ' + (index + 1), { subtype: 'cylinderInfo' }, slide);

        uuid = THREE.Math.generateUUID();
        layerData.objects[uuid] = {
            type: 'cylinderInfo',
            cylinder: cylinder.uuid,
        };

        applyValidOrderToLayerData(slide);
    }

    return uuid;
}

AddInteractiveUI.terrainCar = function (terrainCar, slide) {
    if (slide === undefined) slide = SlideManager.activeSlide;

    let uuid;

    if (slide === SlideManager.activeSlide) {
        let index = Q3.layers.graph.length;
        const selectedLayer = create2DLayer('Car ' + (index + 1), { subtype: 'terrainCarUi' });

        const ui = new Q3.TerrainCarUI({
            terrainCar: terrainCar.uuid,
            index: selectedLayer.objects.length,
        }, selectedLayer, THREE.Math.generateUUID());

        selectedLayer.objects[ui.index] = ui;
        selectedLayer.level = 1;

        const layerData = Q3.data.layers.layers2D[selectedLayer.uuid];
        layerData.objects = layerData.objects || {};

        layerData.objects[ui.uuid] = {
            type: 'terrainCarUi',
            terrainCar: terrainCar.uuid,
        };

        applyDefaultOrder(Q3.layers.graph, selectedLayer.uuid);

        uuid = selectedLayer.uuid;
    } else {
        let index = Q3.layers.minimumUserLayerIndex;
        for (let uuid in Q3.data.slides[slide].layers2D) {
            index++;
        }

        let layerData = create2DLayerData('Car ' + (index + 1), { subtype: 'terrainCarUi' }, slide);

        uuid = THREE.Math.generateUUID();
        layerData.objects[uuid] = {
            type: 'terrainCarUi',
            terrainCar: terrainCar.uuid,
        };

        applyValidOrderToLayerData(slide);
    }

    return uuid;
}

export default AddInteractiveUI;
export { AddInteractiveUI };
